import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, Heart, Shield, Phone, Camera, Watch, Home, Activity } from 'lucide-react';
import ImageService from '../services/ImageService';

const ElderlyHomeCareServicesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Services' },
    { id: 'health', name: 'Health Monitoring' },
    { id: 'maintenance', name: 'Home Maintenance' },
    { id: 'communication', name: 'Communication' },
    { id: 'delivery', name: 'Delivery Services' },
    { id: 'cleaning', name: 'Housekeeping' },
    { id: 'technology', name: 'Safety Technology' }
  ];

  const services = [
    {
      id: 1,
      number: '01',
      name: 'Basic Health Checks',
      category: 'health',
      description: 'Routine monitoring of vital signs and overall well-being',
      image: ImageService.getImageUrl('health-check.jpg'),
      icon: Activity,
      serviceTypes: [
        'Blood pressure monitoring',
        'Temperature checks',
        'Pulse rate monitoring',
        'Weight tracking',
        'Medication reminders',
        'Health status reporting'
      ]
    },
    {
      id: 2,
      number: '02',
      name: 'Preventive Home Maintenance',
      category: 'maintenance',
      description: 'Visual inspection and upkeep to ensure a safe living environment',
      image: ImageService.getImageUrl('home-maintenance.jpg'),
      icon: Home,
      serviceTypes: [
        'Safety hazard inspection',
        'Handrail stability checks',
        'Lighting assessment',
        'Floor safety evaluation',
        'Emergency exit clearance',
        'Basic repair identification'
      ]
    },
    {
      id: 3,
      number: '03',
      name: 'Daily Check-In Call',
      category: 'communication',
      description: 'Phone or WhatsApp contact for reassurance and immediate updates',
      image: ImageService.getImageUrl('phone-call.jpg'),
      icon: Phone,
      serviceTypes: [
        'Morning wellness check',
        'Evening safety call',
        'Emergency contact alerts',
        'Family updates',
        'Medication reminders',
        'Emotional support'
      ]
    },
    {
      id: 4,
      number: '04',
      name: 'Grocery Restock',
      category: 'delivery',
      description: 'Delivery of essentials to maintain daily convenience',
      image: ImageService.getImageUrl('grocery-delivery.jpg'),
      icon: Shield,
      serviceTypes: [
        'Weekly grocery delivery',
        'Pharmacy pickups',
        'Fresh produce delivery',
        'Household essentials',
        'Special dietary items',
        'Emergency supply runs'
      ]
    },
    {
      id: 5,
      number: '05',
      name: 'Housekeeping',
      category: 'cleaning',
      description: 'Includes laundry, sweeping, mopping, light cleaning, and linen change',
      image: ImageService.getImageUrl('housekeeping.jpg'),
      icon: Home,
      serviceTypes: [
        'Laundry and folding',
        'Floor sweeping and mopping',
        'Light dusting and cleaning',
        'Bed linen changes',
        'Bathroom cleaning',
        'Kitchen maintenance'
      ]
    },
    {
      id: 6,
      number: '06',
      name: '24-Hour AI Camera',
      category: 'technology',
      description: 'Privacy-compliant monitoring to ensure safety at home',
      image: ImageService.getImageUrl('ai-camera.jpg'),
      icon: Camera,
      serviceTypes: [
        'Fall detection monitoring',
        'Unusual activity alerts',
        'Emergency response triggers',
        'Privacy-focused recording',
        'Family notification system',
        '24/7 monitoring service'
      ]
    },
    {
      id: 7,
      number: '07',
      name: 'Fitbit Emergency Response',
      category: 'technology',
      description: 'Wearable device to detect falls or emergencies',
      image: ImageService.getImageUrl('fitbit-emergency.jpg'),
      icon: Watch,
      serviceTypes: [
        'Fall detection alerts',
        'Heart rate monitoring',
        'Emergency button activation',
        'GPS location tracking',
        'Automatic emergency calls',
        'Health data tracking'
      ]
    }
  ];

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Hero Section - New Modern Style */}
      <section className="relative overflow-hidden">
        {/* Background with geometric patterns */}
        <div className="absolute inset-0 bg-orange-600">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700"></div>
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-700 rounded-full opacity-30 transform -translate-x-24 translate-y-24"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-24">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-orange-500 bg-opacity-30 rounded-full text-orange-100 text-sm font-medium mb-4 backdrop-blur-sm">
                <Heart className="inline w-4 h-4 mr-2" />
                Elderly Care Services
              </span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            >
              Compassionate Care
              <br />
              <span className="text-orange-200">At Home</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-orange-100 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Comprehensive elderly home care services designed to ensure safety, comfort, and peace of mind for seniors and their families.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
            >
              <Link
                to="/contact"
                className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
              >
                Get Started Today
              </Link>
              <Link
                to="/about"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors"
              >
                Learn More
              </Link>
            </motion.div>
            
            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="grid grid-cols-3 gap-8 max-w-md mx-auto"
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-orange-200 text-sm">Care Support</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">100+</div>
                <div className="text-orange-200 text-sm">Families Served</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">100%</div>
                <div className="text-orange-200 text-sm">Peace of Mind</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ✅ Content Container */}
      <div className="container mx-auto px-4 py-12">

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search care services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden"
              >
                {/* Service Number and Icon */}
                <div className="px-6 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-4xl font-bold text-gray-300">{service.number}</div>
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <IconComponent className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </div>

                {/* Service Image */}
                <div className="px-6 mb-4">
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center" style={{display: 'none'}}>
                      <IconComponent className="w-16 h-16 text-orange-600" />
                    </div>
                  </div>
                </div>

                {/* Service Content */}
                <div className="px-6 pb-6">
                  {/* Service Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4 font-roboto">{service.name}</h3>

                  {/* Service Description */}
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>

                  {/* Service Types List */}
                  <ul className="space-y-2 mb-6">
                    {service.serviceTypes.map((type, idx) => (
                      <li key={idx} className="text-gray-700 flex items-center font-roboto text-sm">
                        <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-3"></span>
                        {type}
                      </li>
                    ))}
                  </ul>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <Link
                      to={`/elderly-order-request?service=${service.id}&title=${encodeURIComponent(service.name)}&category=elderly-care`}
                      className="block w-full bg-orange-600 text-white text-center py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium"
                    >
                      Book Now
                    </Link>
                    <Link
                      to={`/elderly-care/${service.id}`}
                      className="block w-full border-2 border-orange-600 text-orange-600 text-center py-3 rounded-lg hover:bg-orange-50 transition-colors font-medium"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* No results */}
        {filteredServices.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">No services found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-orange-600 rounded-lg p-8 text-center text-white"
        >
          <Heart className="w-12 h-12 mx-auto mb-4 text-orange-200" />
          <h3 className="text-2xl font-bold mb-4">Need Personalized Care?</h3>
          <p className="text-orange-100 mb-6">
            Every senior has unique needs. Contact us to create a customized care plan that ensures safety, comfort, and independence at home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/tel:+1234567890"
              className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors"
            >
              Call Now: (123) 456-7890
            </Link>
          </div>
        </motion.div>

        {/* Trust Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-12 bg-white rounded-lg p-8 shadow-md"
        >
          <div className="text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-orange-600" />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Trusted Care You Can Depend On</h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-600">
              <div className="text-center">
                <div className="font-semibold text-gray-900 mb-2">Licensed & Insured</div>
                <p>All our care providers are fully licensed and insured for your peace of mind.</p>
              </div>
              <div className="text-center">
                <div className="font-semibold text-gray-900 mb-2">24/7 Support</div>
                <p>Round-the-clock emergency support and monitoring for immediate assistance.</p>
              </div>
              <div className="text-center">
                <div className="font-semibold text-gray-900 mb-2">Family Updates</div>
                <p>Regular communication and updates to keep families informed and connected.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ElderlyHomeCareServicesPage;